/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:01]
Permissions to update outside the main branch: [false]
*/

import { Button, DropDown, Form, Modal, Tab } from '@finbeat/react-toolkit';
import { QuestionaryInfo } from 'models/api/questionary';
import { Key, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, TabContent } from 'reactstrap';
import { useQuestionaryWorkplaceState } from '../Questionary';
import QuestionaryModalMain from './QuestionaryModalMain';
import QuestionaryModalQuestionaries from './QuestionaryModalQuestionaries';

export type DropDownForm = Omit<DropDown, 'label'>;

const initialItem: QuestionaryInfo = {
    id: 0,
    name: '',
    dsc: '',
    tradeOrgs: [],
    pages: [],
};

const QuestionaryModal = ({ isOpen = false, toggle }: { isOpen: boolean; toggle: () => void }) => {
    const className = 'questionary-modal';
    const { t } = useTranslation();
    const { currentItem, changeItemMutation, addItemMutation } = useQuestionaryWorkplaceState();

    const innerRef = useRef(null);

    const [activeTab, setActiveTab] = useState('main');
    const defaultValues = useMemo<QuestionaryInfo>(() => currentItem ?? initialItem, [currentItem]);

    const saveChanges = async (data: QuestionaryInfo) => {
        const changeMutation = changeItemMutation;
        const addMutation = addItemMutation;

        (data.id ? changeMutation : addMutation).mutate(data);

        toggle();
    };

    const toggleTab = (value?: Key) => {
        setActiveTab(value?.toString() ?? '');
    };

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={toggle}
            scrollable
            innerRef={innerRef}
            data-testid="questionary-modal"
        >
            <Form<QuestionaryInfo>
                onSubmit={(data) => saveChanges(data)}
                defaultValues={defaultValues}
                className="form-in-modal"
            >
                <ModalHeader toggle={toggle}>
                    {currentItem?.id ? `#${currentItem?.id}` : t('BackOfficeNew')}
                </ModalHeader>

                <ModalBody className="custom-scrollbar">
                    <Nav className={`${className}__nav`} tabs>
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('main')}
                                active={activeTab === 'main'}
                            >
                                {t('BackOfficeMainPageName')}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('questionary')}
                                active={activeTab === 'questionary'}
                            >
                                {t('BackOfficeQuestionary')}
                            </NavLink>
                        </NavItem>
                    </Nav>
                    <TabContent activeTab={activeTab}>
                        <Tab toggle={toggleTab} tabId="main">
                            <QuestionaryModalMain innerRef={innerRef} />
                        </Tab>
                        <Tab toggle={toggleTab} tabId="questionary">
                            <QuestionaryModalQuestionaries />
                        </Tab>
                    </TabContent>
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" type="button" onClick={toggle}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button color="success" type="submit">
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default QuestionaryModal;
